import { useEffect, useRef, useState } from 'react'

export const PLAYBACK_SPEEDS = [1, 1.5, 2] as const

interface UseVoicePlaybackOptions {
  /** Total length in seconds. */
  duration: number
  /** Only one recording plays at a time — parent tracks which one is active. */
  isActive: boolean
  onActivate: () => void
  onDeactivate: () => void
}

/** Simulated audio playback: play/pause, seek, and 1× / 1.5× / 2× speed. */
export function useVoicePlayback({
  duration,
  isActive,
  onActivate,
  onDeactivate,
}: UseVoicePlaybackOptions) {
  const [playing, setPlaying] = useState(false)
  const [progress, setProgress] = useState(0) // 0..1
  const [speedIdx, setSpeedIdx] = useState(0)
  const draggingRef = useRef(false)
  const onDeactivateRef = useRef(onDeactivate)
  onDeactivateRef.current = onDeactivate
  const speed = PLAYBACK_SPEEDS[speedIdx]

  // Another recording started → stop this one.
  useEffect(() => {
    if (!isActive) {
      setPlaying(false)
      setProgress(0)
    }
  }, [isActive])

  useEffect(() => {
    if (!playing) return
    let last = performance.now()
    let frame = 0
    const tick = (now: number) => {
      const delta = (now - last) / 1000
      last = now
      if (!draggingRef.current) {
        setProgress((p) => {
          const next = p + (delta * speed) / duration
          if (next >= 1) {
            setPlaying(false)
            window.setTimeout(() => onDeactivateRef.current(), 0)
            return 0
          }
          return next
        })
      }
      frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [playing, speed, duration])

  const togglePlay = () => {
    if (playing) {
      setPlaying(false)
      return
    }
    onActivate()
    setPlaying(true)
  }

  return {
    playing,
    progress,
    setProgress,
    speed,
    cycleSpeed: () => setSpeedIdx((i) => (i + 1) % PLAYBACK_SPEEDS.length),
    togglePlay,
    draggingRef,
    elapsed: Math.round(progress * duration),
    showPlayer: isActive && (playing || progress > 0),
  }
}

export function formatVoiceTime(seconds: number) {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

/** Deterministic, natural-looking waveform seeded from any string. */
export function buildWaveform(seedText: string, barCount: number) {
  let seed = 0
  for (let i = 0; i < seedText.length; i++)
    seed = (seed * 31 + seedText.charCodeAt(i)) >>> 0
  return Array.from({ length: barCount }, (_, i) => {
    seed = (seed * 1103515245 + 12345) >>> 0
    const noise = (seed % 1000) / 1000
    const envelope = Math.sin((i / (barCount - 1)) * Math.PI) * 0.5 + 0.5
    return Math.round(22 + noise * 58 * envelope + 12 * envelope)
  })
}
